import { useState, useEffect, useCallback } from "react";
import api, { extractErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { useFormatters } from "../components/shared/useFormatters";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Mail,
  Plus,
  Pencil,
  Trash2,
  Send,
  CheckCircle2,
  XCircle,
  Loader2,
  Settings,
  History,
  Shield,
  Download,
  TestTube2,
  Inbox,
  Eye,
  EyeOff,
} from "lucide-react";

// EF-INBOX-THREAD-DETECTION-V1: Password Preview Component
const PasswordPreview = ({ providerId }) => {
  const [visible, setVisible] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchPreview = async () => {
    if (preview) {
      setVisible(!visible);
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.get(`/settings/email/providers/${providerId}/password-preview`);
      setPreview(response.data);
      setVisible(true);
    } catch (error) {
      console.error('Failed to fetch password preview');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-1">
      <button
        onClick={fetchPreview}
        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
        data-testid={`password-preview-toggle-${providerId}`}
      >
        {loading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : visible ? (
          <EyeOff className="h-3 w-3" />
        ) : (
          <Eye className="h-3 w-3" />
        )}
        {visible ? "Hide" : "Show"} Preview
      </button>
      {visible && preview && (
        <div className="mt-1 space-y-1 text-xs font-mono">
          {preview.smtp_password_preview && (
            <p className="text-muted-foreground">SMTP: {preview.smtp_password_preview}</p>
          )}
          {preview.imap_password_preview && (
            <p className="text-muted-foreground">IMAP: {preview.imap_password_preview}</p>
          )}
        </div>
      )}
    </div>
  );
};

const PROVIDER_TYPES = [
  { value: "resend", label: "Resend", description: "Modern email API" },
  { value: "sendgrid", label: "SendGrid", description: "Twilio SendGrid" },
  { value: "smtp", label: "SMTP", description: "Custom SMTP server" },
  { value: "google_smtp", label: "Google Workspace Mail (SMTP + IMAP)", description: "Gmail via App Password - Send & Receive" },
];

export default function EmailSettingsPage() {
  const { formatDate, formatTime } = useFormatters();
  const [providers, setProviders] = useState([]);
  const [testLogs, setTestLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [testingProvider, setTestingProvider] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [activeTab, setActiveTab] = useState("providers");

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    provider_type: "resend",
    from_name: "",
    from_email: "",
    reply_to_email: "",
    enabled: true,
    is_default: false,
    api_key: "",
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    smtp_use_tls: true,
    // EF-EMAIL-PROVIDER-IMAP-INTEGRATION-V1: IMAP fields
    imap_host: "",
    imap_port: 993,
    imap_username: "",
    imap_password: "",
    imap_folder: "INBOX",
    imap_use_ssl: true,
  });

  const [testEmail, setTestEmail] = useState("");
  
  // Email Test State (EF-EMAIL-SEND-RECEIVE-TEST-V1)
  const [smtpTestLoading, setSmtpTestLoading] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  const [imapTestLoading, setImapTestLoading] = useState(false);
  const [imapTestResult, setImapTestResult] = useState(null);
  const [fetchEmailsLoading, setFetchEmailsLoading] = useState(false);
  const [fetchEmailsResult, setFetchEmailsResult] = useState(null);

  const fetchProviders = useCallback(async () => {
    try {
      const response = await api.get("/settings/email/providers");
      setProviders(response.data);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }, []);

  const fetchTestLogs = useCallback(async () => {
    try {
      const response = await api.get("/settings/email/test-logs");
      setTestLogs(response.data);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchProviders(), fetchTestLogs()]);
      setLoading(false);
    };
    loadData();
  }, [fetchProviders, fetchTestLogs]);

  const resetForm = () => {
    setFormData({
      name: "",
      provider_type: "resend",
      from_name: "",
      from_email: "",
      reply_to_email: "",
      enabled: true,
      is_default: false,
      api_key: "",
      smtp_host: "",
      smtp_port: 587,
      smtp_username: "",
      smtp_password: "",
      smtp_use_tls: true,
      // EF-EMAIL-PROVIDER-IMAP-INTEGRATION-V1: IMAP fields
      imap_host: "",
      imap_port: 993,
      imap_username: "",
      imap_password: "",
      imap_folder: "INBOX",
      imap_use_ssl: true,
    });
    setEditingProvider(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (provider) => {
    setEditingProvider(provider);
    setFormData({
      name: provider.name,
      provider_type: provider.provider_type,
      from_name: provider.from_name,
      from_email: provider.from_email,
      reply_to_email: provider.reply_to_email || "",
      enabled: provider.enabled,
      is_default: provider.is_default,
      api_key: "", // Don't populate - security
      smtp_host: provider.smtp_host || "",
      smtp_port: provider.smtp_port || 587,
      smtp_username: provider.smtp_username || "",
      smtp_password: "", // Don't populate - security
      smtp_use_tls: provider.smtp_use_tls ?? true,
      // EF-EMAIL-PROVIDER-IMAP-INTEGRATION-V1: IMAP fields
      imap_host: provider.imap_host || "",
      imap_port: provider.imap_port || 993,
      imap_username: provider.imap_username || "",
      imap_password: "", // Don't populate - security
      imap_folder: provider.imap_folder || "INBOX",
      imap_use_ssl: provider.imap_use_ssl ?? true,
    });
    setDialogOpen(true);
  };

  const openTestDialog = (provider) => {
    setTestingProvider(provider);
    setTestEmail("");
    setTestDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = { ...formData };
      
      // EF-EMAIL-PASSWORD-TYPE-CLARITY-V1: Validation for Google providers
      if (payload.provider_type === "google_smtp") {
        // Check if IMAP is configured but missing password
        const hasImapConfig = payload.imap_host && payload.imap_username;
        const hasImapPassword = payload.imap_password || (editingProvider?.imap_password_set && !payload.imap_password);
        
        if (hasImapConfig && !hasImapPassword && !editingProvider?.imap_password_set) {
          toast.warning("Gmail IMAP requires a Google App Password. IMAP receiving will not work without it.");
        }
      }
      
      // Remove empty optional fields
      if (!payload.reply_to_email) delete payload.reply_to_email;
      
      // EF-GMAIL-SMTP-PERSISTENCE-FIX-01: Handle provider-specific fields
      // EF-EMAIL-PROVIDER-IMAP-INTEGRATION-V1: Include IMAP fields for SMTP providers
      if (payload.provider_type === "google_smtp") {
        // Google SMTP needs smtp_username and smtp_password, but NOT host/port/tls (auto-configured by backend)
        delete payload.smtp_host;
        delete payload.smtp_port;
        delete payload.smtp_use_tls;
        delete payload.api_key;
        // Keep smtp_username and smtp_password - they are required for Google SMTP
        // Keep IMAP fields - they can be used for receiving emails
      } else if (payload.provider_type === "smtp") {
        // Custom SMTP needs all SMTP fields, but NOT api_key
        delete payload.api_key;
        // Keep IMAP fields - they can be used for receiving emails
      } else {
        // API providers (Resend, SendGrid) - remove all SMTP and IMAP fields
        delete payload.smtp_host;
        delete payload.smtp_port;
        delete payload.smtp_username;
        delete payload.smtp_password;
        delete payload.smtp_use_tls;
        delete payload.imap_host;
        delete payload.imap_port;
        delete payload.imap_username;
        delete payload.imap_password;
        delete payload.imap_folder;
        delete payload.imap_use_ssl;
      }
      
      // When editing, only include credentials if they've been changed
      if (editingProvider) {
        if (!payload.api_key) delete payload.api_key;
        if (!payload.smtp_password) delete payload.smtp_password;
        if (!payload.imap_password) delete payload.imap_password;
        
        await api.put(`/settings/email/providers/${editingProvider.id}`, payload);
        toast.success("Provider updated successfully");
      } else {
        await api.post("/settings/email/providers", payload);
        toast.success("Provider created successfully");
      }

      setDialogOpen(false);
      resetForm();
      fetchProviders();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (provider) => {
    if (!window.confirm(`Delete provider "${provider.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/settings/email/providers/${provider.id}`);
      toast.success("Provider deleted");
      fetchProviders();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleToggleEnabled = async (provider) => {
    try {
      await api.put(`/settings/email/providers/${provider.id}`, {
        enabled: !provider.enabled,
      });
      toast.success(`Provider ${provider.enabled ? "disabled" : "enabled"}`);
      fetchProviders();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleSetDefault = async (provider) => {
    try {
      await api.put(`/settings/email/providers/${provider.id}`, {
        is_default: true,
      });
      toast.success(`"${provider.name}" is now the default provider`);
      fetchProviders();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleSendTest = async (e) => {
    e.preventDefault();
    if (!testEmail || !testingProvider) return;

    setSendingTest(true);
    try {
      const response = await api.post("/settings/email/test", {
        provider_id: testingProvider.id,
        test_email: testEmail,
      });

      if (response.data.success) {
        toast.success("Test email sent successfully!");
        setTestDialogOpen(false);
      } else {
        // Show detailed error with diagnostics if available
        const diagnostics = response.data.diagnostics;
        if (diagnostics) {
          const diagInfo = [
            `Username: ${diagnostics.username_used}`,
            `Password Length: ${diagnostics.password_length} (${diagnostics.password_is_16_chars ? "✓ Valid" : "✗ Gmail requires 16 chars"})`,
            `Password Preview: ${diagnostics.password_preview}`,
            `ASCII Check: ${diagnostics.ascii_check_passed ? "✓ Passed" : "✗ Failed"}`
          ].join("\n");
          
          toast.error(
            <div>
              <p className="font-semibold">Test Failed</p>
              <p className="text-sm mt-1">{response.data.error}</p>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-muted-foreground">Show Diagnostics</summary>
                <pre className="text-xs mt-1 whitespace-pre-wrap">{diagInfo}</pre>
              </details>
              <p className="text-xs mt-2 text-yellow-500">
                → Generate a new App Password at myaccount.google.com/apppasswords
              </p>
            </div>,
            { duration: 10000 }
          );
        } else {
          toast.error(`Test failed: ${response.data.error}`);
        }
      }

      fetchTestLogs();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setSendingTest(false);
    }
  };

  // EF-EMAIL-SEND-RECEIVE-TEST-V1: Handler for Send Test Email
  const handleSendTestEmail = async () => {
    setSmtpTestLoading(true);
    setSmtpTestResult(null);
    
    try {
      const response = await api.post("/email/test-smtp-quick");
      setSmtpTestResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message || "Test email sent successfully!");
      } else {
        toast.error(response.data.message || "Failed to send test email");
      }
    } catch (error) {
      const errorMsg = extractErrorMessage(error);
      setSmtpTestResult({ success: false, message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setSmtpTestLoading(false);
    }
  };

  // EF-EMAIL-SEND-RECEIVE-TEST-V1: Handler for Receive Test Email (IMAP test)
  // EF-EMAIL-PROVIDER-IMAP-INTEGRATION-V1: Updated to use provider IMAP config
  const handleReceiveTestEmail = async () => {
    setImapTestLoading(true);
    setImapTestResult(null);
    
    try {
      // Find default provider with IMAP configured
      const providerWithImap = providers.find(p => p.is_default && p.imap_host) 
        || providers.find(p => p.enabled && p.imap_host);
      
      let response;
      if (providerWithImap) {
        // Use provider-based IMAP test
        response = await api.post(`/email/test-imap-provider/${providerWithImap.id}`);
      } else {
        // Fallback to legacy platform settings-based test
        response = await api.post("/email/test-imap-saved");
      }
      
      setImapTestResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message || "IMAP connection successful!");
      } else {
        toast.error(response.data.message || "IMAP connection failed");
      }
    } catch (error) {
      const errorMsg = extractErrorMessage(error);
      setImapTestResult({ success: false, message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setImapTestLoading(false);
    }
  };

  // EF-EMAIL-SEND-RECEIVE-TEST-V1: Handler for Fetch Latest Emails
  const handleFetchLatestEmails = async () => {
    setFetchEmailsLoading(true);
    setFetchEmailsResult(null);
    
    try {
      const response = await api.post("/email/fetch-now");
      setFetchEmailsResult(response.data);
      
      if (response.data.success) {
        const newCount = response.data.new_count || 0;
        const skipped = response.data.skipped_count || 0;
        toast.success(`${newCount} new emails imported (${skipped} skipped)`);
      } else {
        toast.error(response.data.error || "Failed to fetch emails");
      }
    } catch (error) {
      const errorMsg = extractErrorMessage(error);
      setFetchEmailsResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setFetchEmailsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-settings-page">
      {/* Header - EF-SETTINGS-CONSOLIDATION-V1: Added breadcrumb */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <a href="/settings" className="hover:text-foreground">Settings</a>
            <span>/</span>
            <span className="text-foreground">Email Configuration</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Email Configuration</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Configure email providers for notifications and communications
          </p>
        </div>
        <Button onClick={openCreateDialog} data-testid="add-provider-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Provider
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        <button
          onClick={() => setActiveTab("providers")}
          className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            activeTab === "providers"
              ? "bg-primary/10 text-primary border-b-2 border-primary"
              : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid="tab-providers"
        >
          <Settings className="h-4 w-4 inline mr-2" />
          Providers
        </button>
        <button
          onClick={() => setActiveTab("logs")}
          className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            activeTab === "logs"
              ? "bg-primary/10 text-primary border-b-2 border-primary"
              : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid="tab-logs"
        >
          <History className="h-4 w-4 inline mr-2" />
          Test Logs
        </button>
        <button
          onClick={() => setActiveTab("test")}
          className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${
            activeTab === "test"
              ? "bg-primary/10 text-primary border-b-2 border-primary"
              : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid="tab-test"
        >
          <TestTube2 className="h-4 w-4 inline mr-2" />
          Email Test
        </button>
      </div>

      {/* Providers Tab */}
      {activeTab === "providers" && (
        <div className="space-y-4">
          {providers.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Mail className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No providers configured</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Add an email provider to start sending notifications
                </p>
                <Button onClick={openCreateDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Provider
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {providers.map((provider) => (
                <Card key={provider.id} className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-md ${provider.enabled ? "bg-primary/10" : "bg-surface-elevated"}`}>
                          <Mail className={`h-5 w-5 ${provider.enabled ? "text-primary" : "text-muted-foreground"}`} />
                        </div>
                        <div>
                          <CardTitle className="text-base flex items-center gap-2">
                            {provider.name}
                            {provider.is_default && (
                              <Badge variant="secondary" className="text-xs">
                                <Shield className="h-3 w-3 mr-1" />
                                Default
                              </Badge>
                            )}
                          </CardTitle>
                          <CardDescription className="text-xs">
                            {PROVIDER_TYPES.find((t) => t.value === provider.provider_type)?.label || provider.provider_type}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={provider.enabled}
                          onCheckedChange={() => handleToggleEnabled(provider)}
                          data-testid={`toggle-${provider.id}`}
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                      <div>
                        <span className="text-muted-foreground">From</span>
                        <p className="font-mono text-xs truncate">{provider.from_email}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">From Name</span>
                        <p className="truncate">{provider.from_name}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Credentials</span>
                        <p className="flex items-center gap-1">
                          {provider.provider_type === "smtp" || provider.provider_type === "google_smtp" ? (
                            provider.smtp_password_set ? (
                              <><CheckCircle2 className="h-3 w-3 text-green-500" /> Configured</>
                            ) : (
                              <><XCircle className="h-3 w-3 text-red-500" /> Not set</>
                            )
                          ) : (
                            provider.api_key_set ? (
                              <><CheckCircle2 className="h-3 w-3 text-green-500" /> Configured</>
                            ) : (
                              <><XCircle className="h-3 w-3 text-red-500" /> Not set</>
                            )
                          )}
                        </p>
                        {/* EF-INBOX-THREAD-DETECTION-V1: Password preview toggle */}
                        {(provider.smtp_password_set || provider.imap_password_set) && (
                          <PasswordPreview providerId={provider.id} />
                        )}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Status</span>
                        <div>
                          <Badge variant={provider.enabled ? "default" : "secondary"}>
                            {provider.enabled ? "Enabled" : "Disabled"}
                          </Badge>
                        </div>
                      </div>
                      {/* EF-EMAIL-PROVIDER-IMAP-INTEGRATION-V1: Show IMAP status */}
                      {(provider.provider_type === "smtp" || provider.provider_type === "google_smtp") && (
                        <div>
                          <span className="text-muted-foreground">IMAP</span>
                          <p className="flex items-center gap-1">
                            {provider.imap_host && provider.imap_password_set ? (
                              <><CheckCircle2 className="h-3 w-3 text-blue-500" /> {provider.imap_host}</>
                            ) : provider.imap_host ? (
                              <><XCircle className="h-3 w-3 text-amber-500" /> No password</>
                            ) : (
                              <span className="text-muted-foreground text-xs">Not configured</span>
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 border-t border-white/5 pt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openTestDialog(provider)}
                        disabled={!provider.enabled}
                        data-testid={`test-${provider.id}`}
                      >
                        <Send className="h-3 w-3 mr-1" />
                        Test Send
                      </Button>
                      {/* EF-EMAIL-PROVIDER-IMAP-INTEGRATION-V1: IMAP test button */}
                      {provider.imap_host && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              setImapTestLoading(true);
                              const response = await api.post(`/email/test-imap-provider/${provider.id}`);
                              setImapTestResult(response.data);
                              if (response.data.success) {
                                toast.success("IMAP connection successful!");
                              } else {
                                toast.error(response.data.message || "IMAP test failed");
                              }
                            } catch (error) {
                              toast.error(extractErrorMessage(error));
                            } finally {
                              setImapTestLoading(false);
                            }
                          }}
                          disabled={!provider.enabled || imapTestLoading}
                          data-testid={`test-imap-${provider.id}`}
                        >
                          <Inbox className="h-3 w-3 mr-1" />
                          Test Receive
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(provider)}
                        data-testid={`edit-${provider.id}`}
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      {!provider.is_default && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSetDefault(provider)}
                          data-testid={`set-default-${provider.id}`}
                        >
                          Set Default
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto text-destructive hover:text-destructive"
                        onClick={() => handleDelete(provider)}
                        data-testid={`delete-${provider.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Test Logs Tab */}
      {activeTab === "logs" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Test Email Log</CardTitle>
            <CardDescription>
              Read-only audit trail of all email test attempts
            </CardDescription>
          </CardHeader>
          <CardContent>
            {testLogs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No test emails sent yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Test Email</TableHead>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {testLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          {log.status === "success" ? (
                            <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Success
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <XCircle className="h-3 w-3 mr-1" />
                              Failed
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{log.provider_name}</span>
                          <br />
                          <span className="text-xs text-muted-foreground uppercase">
                            {log.provider_type}
                          </span>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{log.test_email}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDate(log.created_at)} {formatTime(log.created_at)}
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-xs text-red-400">
                          {log.error_message || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Email Test Tab (EF-EMAIL-SEND-RECEIVE-TEST-V1) */}
      {activeTab === "test" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <TestTube2 className="h-5 w-5" />
                Email Test
              </CardTitle>
              <CardDescription>
                Test your SMTP and IMAP configuration before using the Inbox
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* SMTP Send Test */}
              <div className="p-4 bg-surface-elevated/30 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Send Test Email</h4>
                    <p className="text-sm text-muted-foreground">
                      Sends a test email to verify SMTP configuration
                    </p>
                  </div>
                  <Button
                    onClick={handleSendTestEmail}
                    disabled={smtpTestLoading}
                    data-testid="btn-send-test-email"
                  >
                    {smtpTestLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Send Test Email
                  </Button>
                </div>
                
                {smtpTestResult && (
                  <div className={`p-3 rounded-md ${smtpTestResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <div className="flex items-center gap-2">
                      {smtpTestResult.success ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                      <span className={smtpTestResult.success ? 'text-green-400' : 'text-red-400'}>
                        {smtpTestResult.message}
                      </span>
                    </div>
                    {smtpTestResult.details && (
                      <div className="mt-2 text-xs text-muted-foreground space-y-1">
                        <p>Host: {smtpTestResult.details.host}</p>
                        <p>To: {smtpTestResult.details.to}</p>
                        <p>Subject: {smtpTestResult.details.subject}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* IMAP Receive Test */}
              <div className="p-4 bg-surface-elevated/30 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Receive Test Email</h4>
                    <p className="text-sm text-muted-foreground">
                      Tests IMAP connection and retrieves latest email metadata
                    </p>
                  </div>
                  <Button
                    onClick={handleReceiveTestEmail}
                    disabled={imapTestLoading}
                    variant="outline"
                    data-testid="btn-receive-test-email"
                  >
                    {imapTestLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Receive Test Email
                  </Button>
                </div>
                
                {imapTestResult && (
                  <div className={`p-3 rounded-md ${imapTestResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <div className="flex items-center gap-2">
                      {imapTestResult.success ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                      <span className={imapTestResult.success ? 'text-green-400' : 'text-red-400'}>
                        {imapTestResult.message}
                      </span>
                    </div>
                    {imapTestResult.success && imapTestResult.email_count !== undefined && (
                      <div className="mt-2 text-xs text-muted-foreground space-y-1">
                        <p>Total emails in folder: {imapTestResult.email_count}</p>
                        {imapTestResult.most_recent_sender && (
                          <>
                            <p className="font-medium mt-2">Latest Email:</p>
                            <p>Sender: {imapTestResult.most_recent_sender}</p>
                            <p>Subject: {imapTestResult.most_recent_subject}</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Fetch Latest Emails */}
              <div className="p-4 bg-surface-elevated/30 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Fetch Latest Emails</h4>
                    <p className="text-sm text-muted-foreground">
                      Import new emails from IMAP into the Inbox queue
                    </p>
                  </div>
                  <Button
                    onClick={handleFetchLatestEmails}
                    disabled={fetchEmailsLoading}
                    variant="outline"
                    data-testid="btn-fetch-latest-emails"
                  >
                    {fetchEmailsLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Inbox className="h-4 w-4 mr-2" />
                    )}
                    Fetch Latest Emails
                  </Button>
                </div>
                
                {fetchEmailsResult && (
                  <div className={`p-3 rounded-md ${fetchEmailsResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                    <div className="flex items-center gap-2">
                      {fetchEmailsResult.success ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                      <span className={fetchEmailsResult.success ? 'text-green-400' : 'text-red-400'}>
                        {fetchEmailsResult.success 
                          ? `${fetchEmailsResult.new_count || 0} new emails imported`
                          : (fetchEmailsResult.error || 'Failed to fetch emails')
                        }
                      </span>
                    </div>
                    {fetchEmailsResult.success && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        <p>Skipped (already imported): {fetchEmailsResult.skipped_count || 0}</p>
                        <p>Total fetched from server: {fetchEmailsResult.total_fetched || 0}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <h4 className="font-medium text-blue-400 mb-2">Configuration Instructions</h4>
                <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                  <li>Configure SMTP settings in the <strong>Providers</strong> tab to enable sending</li>
                  <li>Configure IMAP settings below to enable receiving:</li>
                </ol>
                <div className="mt-3 p-3 bg-surface-elevated/50 rounded text-xs font-mono space-y-1">
                  <p>IMAP Host: imap.gmail.com (for Gmail)</p>
                  <p>IMAP Port: 993 (SSL)</p>
                  <p>IMAP Username: your.email@gmail.com</p>
                  <p>IMAP Password: Your App Password</p>
                  <p>Folder: INBOX</p>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Use the API endpoint <code className="bg-surface-elevated px-1 rounded">/api/email/imap-config</code> to save IMAP settings.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create/Edit Provider Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProvider ? "Edit Provider" : "Add Email Provider"}
            </DialogTitle>
            <DialogDescription>
              Configure email provider settings for sending notifications
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Provider Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Production Resend"
                  required
                  data-testid="input-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="provider_type">Provider Type *</Label>
                <Select
                  value={formData.provider_type}
                  onValueChange={(v) => setFormData({ ...formData, provider_type: v })}
                >
                  <SelectTrigger data-testid="select-provider-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVIDER_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="from_name">From Name *</Label>
                <Input
                  id="from_name"
                  value={formData.from_name}
                  onChange={(e) => setFormData({ ...formData, from_name: e.target.value })}
                  placeholder="EventsFlo"
                  required
                  data-testid="input-from-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="from_email">From Email *</Label>
                <Input
                  id="from_email"
                  type="email"
                  value={formData.from_email}
                  onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                  placeholder="noreply@example.com"
                  required
                  data-testid="input-from-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reply_to_email">Reply-To Email (optional)</Label>
              <Input
                id="reply_to_email"
                type="email"
                value={formData.reply_to_email}
                onChange={(e) => setFormData({ ...formData, reply_to_email: e.target.value })}
                placeholder="support@example.com"
                data-testid="input-reply-to"
              />
            </div>

            {/* API Key (for Resend/SendGrid only - NOT for google_smtp) */}
            {formData.provider_type !== "smtp" && formData.provider_type !== "google_smtp" && (
              <div className="space-y-2">
                <Label htmlFor="api_key">
                  API Key {!editingProvider && "*"}
                  {editingProvider && (
                    <span className="text-xs text-muted-foreground ml-2">
                      (Leave blank to keep existing)
                    </span>
                  )}
                </Label>
                <Input
                  id="api_key"
                  type="password"
                  value={formData.api_key}
                  onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                  placeholder={editingProvider ? "••••••••" : "Enter API key"}
                  required={!editingProvider}
                  data-testid="input-api-key"
                />
              </div>
            )}

            {/* Google SMTP Settings (EF-GMAIL-PROVIDER-REFORM-01) */}
            {formData.provider_type === "google_smtp" && (
              <div className="space-y-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <Mail className="h-5 w-5 text-amber-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-sm text-amber-500">Google Send Only</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Requires Gmail App Password (not your normal Google password).
                      <br />
                      Enable 2-Step Verification on your Google account first.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp_username">Gmail Address *</Label>
                    <Input
                      id="smtp_username"
                      type="email"
                      value={formData.smtp_username}
                      onChange={(e) => setFormData({ ...formData, smtp_username: e.target.value })}
                      placeholder="your.email@gmail.com"
                      required
                      data-testid="input-gmail-address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp_password">
                      App Password {!editingProvider && "*"}
                      {editingProvider && (
                        <span className="text-xs text-muted-foreground ml-1">
                          (blank = keep existing)
                        </span>
                      )}
                    </Label>
                    <Input
                      id="smtp_password"
                      type="password"
                      value={formData.smtp_password}
                      onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                      placeholder={editingProvider ? "••••••••" : "Enter app password"}
                      required={!editingProvider}
                      data-testid="input-app-password"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Generate an App Password at{" "}
                  <a 
                    href="https://myaccount.google.com/apppasswords" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Google Account Settings
                  </a>
                </p>
              </div>
            )}

            {/* SMTP Settings (Custom SMTP only) */}
            {formData.provider_type === "smtp" && (
              <div className="space-y-4 p-4 bg-surface-elevated/30 rounded-lg">
                <h4 className="font-medium text-sm">SMTP Configuration</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp_host">SMTP Host *</Label>
                    <Input
                      id="smtp_host"
                      value={formData.smtp_host}
                      onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                      placeholder="smtp.example.com"
                      required
                      data-testid="input-smtp-host"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp_port">Port *</Label>
                    <Input
                      id="smtp_port"
                      type="number"
                      value={formData.smtp_port}
                      onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) })}
                      required
                      data-testid="input-smtp-port"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp_username">Username *</Label>
                    <Input
                      id="smtp_username"
                      value={formData.smtp_username}
                      onChange={(e) => setFormData({ ...formData, smtp_username: e.target.value })}
                      required
                      data-testid="input-smtp-username"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp_password">
                      Password {!editingProvider && "*"}
                    </Label>
                    <Input
                      id="smtp_password"
                      type="password"
                      value={formData.smtp_password}
                      onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                      placeholder={editingProvider ? "••••••••" : "Enter password"}
                      required={!editingProvider}
                      data-testid="input-smtp-password"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="smtp_use_tls"
                    checked={formData.smtp_use_tls}
                    onCheckedChange={(checked) => setFormData({ ...formData, smtp_use_tls: checked })}
                    data-testid="toggle-smtp-tls"
                  />
                  <Label htmlFor="smtp_use_tls" className="text-sm">Use TLS encryption</Label>
                </div>
              </div>
            )}

            {/* EF-EMAIL-PROVIDER-IMAP-INTEGRATION-V1: IMAP Settings (for SMTP providers) */}
            {(formData.provider_type === "smtp" || formData.provider_type === "google_smtp") && (
              <div className="space-y-4 p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <Inbox className="h-4 w-4 text-blue-500" />
                  <h4 className="font-medium text-sm">IMAP Settings (Receiving)</h4>
                </div>
                <p className="text-xs text-muted-foreground">
                  Configure IMAP to receive emails into the inbox queue.
                  {formData.provider_type === "google_smtp" && " For Gmail, use imap.gmail.com port 993."}
                </p>
                {/* EF-EMAIL-PASSWORD-TYPE-CLARITY-V1: Clear instruction for Google providers */}
                {formData.provider_type === "google_smtp" && (
                  <div className="p-3 rounded-md bg-amber-500/10 border border-amber-500/30">
                    <p className="text-xs text-amber-200">
                      <strong>Important:</strong> For Gmail / Google Workspace providers, use the <strong>SAME Google App Password</strong> used for SMTP. Do <strong>NOT</strong> use your Google account password.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="imap_host">IMAP Host</Label>
                    <Input
                      id="imap_host"
                      value={formData.imap_host}
                      onChange={(e) => setFormData({ ...formData, imap_host: e.target.value })}
                      placeholder={formData.provider_type === "google_smtp" ? "imap.gmail.com" : "imap.example.com"}
                      data-testid="input-imap-host"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="imap_port">Port</Label>
                    <Input
                      id="imap_port"
                      type="number"
                      value={formData.imap_port}
                      onChange={(e) => setFormData({ ...formData, imap_port: parseInt(e.target.value) || 993 })}
                      data-testid="input-imap-port"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="imap_username">Username</Label>
                    <Input
                      id="imap_username"
                      value={formData.imap_username}
                      onChange={(e) => setFormData({ ...formData, imap_username: e.target.value })}
                      placeholder={formData.smtp_username || "your-email@gmail.com"}
                      data-testid="input-imap-username"
                    />
                  </div>
                  <div className="space-y-2">
                    {/* EF-EMAIL-PASSWORD-TYPE-CLARITY-V1: Updated label */}
                    <Label htmlFor="imap_password">
                      {formData.provider_type === "google_smtp" ? "App Password" : "Password"}
                    </Label>
                    <Input
                      id="imap_password"
                      type="password"
                      value={formData.imap_password}
                      onChange={(e) => setFormData({ ...formData, imap_password: e.target.value })}
                      placeholder={editingProvider?.imap_password_set ? "••••••••" : (formData.provider_type === "google_smtp" ? "Google App Password" : "Password")}
                      data-testid="input-imap-password"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="imap_folder">Folder</Label>
                    <Input
                      id="imap_folder"
                      value={formData.imap_folder}
                      onChange={(e) => setFormData({ ...formData, imap_folder: e.target.value })}
                      placeholder="INBOX"
                      data-testid="input-imap-folder"
                    />
                  </div>
                  <div className="flex items-end pb-2">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="imap_use_ssl"
                        checked={formData.imap_use_ssl}
                        onCheckedChange={(checked) => setFormData({ ...formData, imap_use_ssl: checked })}
                        data-testid="toggle-imap-ssl"
                      />
                      <Label htmlFor="imap_use_ssl" className="text-sm">Use SSL</Label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 pt-2">
              <div className="flex items-center gap-2">
                <Switch
                  id="enabled"
                  checked={formData.enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                  data-testid="toggle-enabled"
                />
                <Label htmlFor="enabled" className="text-sm">Enabled</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="is_default"
                  checked={formData.is_default}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
                  data-testid="toggle-default"
                />
                <Label htmlFor="is_default" className="text-sm">Set as Default</Label>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-provider-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingProvider ? "Update" : "Create"} Provider
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send a test email using "{testingProvider?.name}" to verify configuration
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendTest} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="test_email">Recipient Email *</Label>
              <Input
                id="test_email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your-email@example.com"
                required
                data-testid="input-test-email"
              />
              <p className="text-xs text-muted-foreground">
                Enter your email address to receive the test message
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setTestDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={sendingTest} data-testid="send-test-btn">
                {sendingTest && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Send className="h-4 w-4 mr-2" />
                Send Test
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
